(function () {
  'use strict';

  const page = (window.FoxTheme && window.FoxTheme.page) || {};

  // Growth Campaign Analytics Module
  const GrowthAnalytics = {
    // 2. Track Product Discovery Method
    trackDiscoveryMethod: function () {
      if (page.pageType !== 'product') return;

      const urlParams = new URLSearchParams(window.location.search);
      const source = urlParams.get('utm_source') || 'direct';
      const medium = urlParams.get('utm_medium') || 'none';
      const campaign = urlParams.get('utm_campaign') || 'none';

      let discoveryMethod = 'organic';

      // Categorize discovery method
      if (['chatgpt', 'claude', 'perplexity', 'gemini', 'copilot'].includes(source)) {
        discoveryMethod = 'ai_agent';
      } else if (medium === 'social' || medium === 'organic_social' || medium === 'paid_social') {
        discoveryMethod = 'social_media';
      } else if (medium === 'email') {
        discoveryMethod = 'email_campaign';
      } else if (source === 'blog' || medium === 'content') {
        discoveryMethod = 'content_marketing';
      } else if (medium === 'influencer') {
        discoveryMethod = 'influencer';
      } else if (medium === 'cpc' || medium === 'paid') {
        discoveryMethod = 'paid_advertising';
      }

      if (window.gtag) {
        gtag('event', 'product_discovery', {
          event_category: 'Discovery',
          event_label: discoveryMethod,
          product_id: page.product && page.product.id,
          product_name: page.product && page.product.title,
          product_price: page.product && page.product.priceAmount,
          utm_source: source,
          utm_medium: medium,
          utm_campaign: campaign,
        });
      }

      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'product_discovery',
          discovery_method: discoveryMethod,
          product_id: page.product && page.product.id,
          product_category: page.product && page.product.type,
          utm_source: source,
          utm_medium: medium,
        });
      }
    },

    // 3. Track Gift Guide Engagement
    setupGiftGuideTracking: function () {
      document.addEventListener('click', function (e) {
        const giftGuideLink = e.target.closest('[data-gift-guide]');

        if (giftGuideLink) {
          const guideName = giftGuideLink.dataset.giftGuide;
          const productId = giftGuideLink.dataset.productId || 'unknown';
          const position = giftGuideLink.dataset.position || 'unknown';

          if (window.gtag) {
            gtag('event', 'gift_guide_click', {
              event_category: 'Gift Guide',
              event_label: guideName,
              product_id: productId,
              position: position,
              guide_type: giftGuideLink.dataset.guideType || 'general',
            });
          }

          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'gift_guide_engagement',
              guide_name: guideName,
              product_id: productId,
              position: position,
              interaction_type: 'click',
              timestamp: Date.now(),
            });
          }
        }
      });
    },

    // 4. Track Personalization Interactions
    setupPersonalizationTracking: function () {
      // Track when AI recommendations are shown
      window.addEventListener('ac:recommendations-shown', function (e) {
        if (window.gtag) {
          gtag('event', 'personalization_shown', {
            event_category: 'Personalization',
            event_label: 'AI Recommendations',
            products_count: e.detail.products.length,
            user_intent: sessionStorage.getItem('ac_intent'),
            page_type: page.pageType,
          });
        }

        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'personalization_shown',
            recommendation_type: 'ai_powered',
            products_count: e.detail.products.length,
            user_intent: sessionStorage.getItem('ac_intent'),
          });
        }
      });

      // Track recommendation clicks
      window.addEventListener('ac:recommendation-click', function (e) {
        if (window.gtag) {
          gtag('event', 'personalization_click', {
            event_category: 'Personalization',
            event_label: 'AI Recommendation',
            product_id: e.detail.productId,
            position: e.detail.position,
            user_intent: sessionStorage.getItem('ac_intent'),
            source: e.detail.source || 'unknown',
          });
        }

        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'personalization_click',
            product_id: e.detail.productId,
            position: e.detail.position,
            recommendation_source: e.detail.source,
          });
        }
      });
    },

    // 5. Track Cart Abandonment Triggers
    setupCartRecoveryTracking: function () {
      // Track exit intent triggers
      window.addEventListener('ac:exit-intent-shown', function (e) {
        if (window.gtag) {
          gtag('event', 'exit_intent_triggered', {
            event_category: 'Cart Recovery',
            event_label: 'Exit Intent Modal',
            cart_value: e.detail.cartValue,
            cart_items: page.cartItemCount,
            page_type: page.pageType,
          });
        }

        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'cart_abandonment_risk',
            abandonment_trigger: 'exit_intent',
            cart_value: e.detail.cartValue,
            cart_items: page.cartItemCount,
            recovery_offered: true,
          });
        }
      });

      // Track scroll-based abandonment risk (cart present but scrolling away)
      // Rides the shared coordinator (assets/scroll-coordinator.js) instead of its own
      // window scroll listener: that listener had no options object at all, so it was
      // not even passive and blocked the compositor on Android touch scroll on top of
      // recomputing scrollHeight on every raw event. Gated behind cart having items,
      // so a real shopper paid for what a browsing visitor never triggered.
      if (page.cartItemCount > 0) {
        let scrollDepth = 0;
        const scrollThresholds = [25, 50, 75, 100];

        window.CoraScroll.subscribe({
          name: 'cart-scroll-abandonment',
          read: function (m) {
            return Math.round((m.scrollY / m.maxScroll) * 100);
          },
          write: function (scrollPercent) {
            scrollThresholds.forEach((threshold) => {
              if (scrollPercent >= threshold && scrollDepth < threshold) {
                scrollDepth = threshold;

                if (window.gtag) {
                  gtag('event', 'cart_scroll_depth', {
                    event_category: 'Cart Recovery',
                    event_label: `${threshold}% Scroll with Cart`,
                    cart_value: page.cartTotalPrice,
                    cart_items: page.cartItemCount,
                    page_type: page.pageType,
                  });
                }
              }
            });
          },
        });
      }
    },

    // 6. Track Search Quality & Zero Results
    trackSearchQuality: function () {
      if (page.pageType !== 'search') return;

      const urlParams = new URLSearchParams(window.location.search);
      const searchQuery = urlParams.get('q') || '';
      const resultsCount = document.querySelectorAll('.product-card, [data-product-card]').length;
      const searchQuality = resultsCount > 0 ? 'results_found' : 'no_results';

      if (window.gtag) {
        gtag('event', 'search_performed', {
          event_category: 'Search',
          event_label: searchQuery,
          search_term: searchQuery,
          results_count: resultsCount,
          search_quality: searchQuality,
        });
      }

      // Track zero-result searches for optimization
      if (resultsCount === 0) {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'zero_results_search',
            search_term: searchQuery,
            page_type: page.pageType,
            timestamp: Date.now(),
          });
        }

        // Also track with gtag for easier reporting
        if (window.gtag) {
          gtag('event', 'zero_results', {
            event_category: 'Search Issues',
            event_label: searchQuery,
            search_term: searchQuery,
          });
        }
      }
    },

    // 7. Track Social Sharing
    setupSocialShareTracking: function () {
      const shareButtons = document.querySelectorAll('[data-share-platform], [data-share]');

      shareButtons.forEach((btn) => {
        btn.addEventListener('click', function () {
          const platform = this.dataset.sharePlatform || this.dataset.share;
          const productId =
            this.dataset.productId || (page.product && page.product.id) || 'unknown';
          const contentType = this.dataset.contentType || 'product';

          if (window.gtag) {
            gtag('event', 'share', {
              method: platform,
              content_type: contentType,
              content_id: productId,
              event_category: 'Social Sharing',
              event_label: platform,
            });
          }

          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'social_share',
              platform: platform,
              shared_content: contentType,
              product_id: productId,
            });
          }
        });
      });
    },

    // 8. Track Email Signup Conversions
    trackEmailSignup: function () {
      const emailForms = document.querySelectorAll(
        'form[action*="klaviyo"], form[action*="newsletter"]'
      );

      emailForms.forEach((form) => {
        form.addEventListener('submit', function () {
          const source = new URLSearchParams(window.location.search).get('utm_source') || 'direct';
          const medium = new URLSearchParams(window.location.search).get('utm_medium') || 'organic';

          if (window.gtag) {
            gtag('event', 'newsletter_signup', {
              event_category: 'Lead Generation',
              event_label: 'Email Signup',
              signup_source: source,
              signup_medium: medium,
              page_type: page.pageType,
            });
          }

          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'newsletter_signup',
              signup_location: this.dataset.location || 'unknown',
              traffic_source: source,
              traffic_medium: medium,
            });
          }
        });
      });
    },

    // 9. Track Collection Filter Usage
    trackFilterUsage: function () {
      if (page.pageType !== 'collection') return;

      const filterButtons = document.querySelectorAll(
        '[data-filter], .facet-checkbox input, .filter-option'
      );

      filterButtons.forEach((filter) => {
        filter.addEventListener('change', function () {
          const filterType = this.dataset.filterType || this.name || 'unknown';
          const filterValue = this.dataset.filterValue || this.value || 'unknown';

          if (window.gtag) {
            gtag('event', 'filter_applied', {
              event_category: 'Collection Browsing',
              event_label: filterType,
              filter_type: filterType,
              filter_value: filterValue,
              collection: (page.collection && page.collection.title) || null,
            });
          }

          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'collection_filter',
              filter_type: filterType,
              filter_value: filterValue,
              collection_handle: (page.collection && page.collection.handle) || null,
            });
          }
        });
      });
    },

    // Initialize all tracking
    init: function () {
      // Run all tracking setups
      this.trackDiscoveryMethod();
      this.setupGiftGuideTracking();
      this.setupPersonalizationTracking();
      this.setupCartRecoveryTracking();
      this.trackSearchQuality();
      this.setupSocialShareTracking();
      this.trackEmailSignup();
      this.trackFilterUsage();

      // Track page view with UTM context
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('utm_source')) {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'utm_pageview',
            utm_source: urlParams.get('utm_source'),
            utm_medium: urlParams.get('utm_medium'),
            utm_campaign: urlParams.get('utm_campaign'),
            utm_content: urlParams.get('utm_content'),
            utm_term: urlParams.get('utm_term'),
            page_type: page.pageType,
            page_path: page.path,
          });
        }
      }
    },
  };

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      GrowthAnalytics.init();
    });
  } else {
    GrowthAnalytics.init();
  }

  // Make available globally for manual tracking
  window.GrowthAnalytics = GrowthAnalytics;
})();
